import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { CandidateSelector } from "@/components/candidates/CandidateSelector";
import { supabase } from "@/integrations/supabase/client";
import {
	Loader2,
	X,
	UserX,
	Users2,
	DollarSign,
	Calendar,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { CURRENCY_OPTIONS, BENCH_REASONS, BenchReason } from "@/types/workforce";

interface AddToBenchModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess?: () => void;
}

interface SelectedCandidate {
	id: string;
	name: string;
}

export const AddToBenchModal = ({ open, onOpenChange, onSuccess }: AddToBenchModalProps) => {
	const { t } = useTranslation();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [organizationUuid, setOrganizationUuid] = useState<string | null>(null);

	// Form state
	const [selectedCandidate, setSelectedCandidate] = useState<SelectedCandidate | null>(null);
	const [benchStartDate, setBenchStartDate] = useState(new Date().toISOString().split('T')[0]);
	const [dailyCost, setDailyCost] = useState("");
	const [currency, setCurrency] = useState("EUR");
	const [reason, setReason] = useState<BenchReason | "">("");
	const [notes, setNotes] = useState("");
	const [workPercentage, setWorkPercentage] = useState("");

	// Fetch organization UUID
	useEffect(() => {
		const fetchOrgUuid = async () => {
			const { data: { user } } = await supabase.auth.getUser();
			if (!user) return;

			const { data: userOrg } = await supabase
				.from('user_organization')
				.select('organization_uuid')
				.eq('user_uuid', user.id)
				.single();

			if (userOrg?.organization_uuid)
				setOrganizationUuid(userOrg.organization_uuid);
		};

		fetchOrgUuid();
	}, []);

	const handleCandidateSelect = (candidate: { id: string; name: string }) => {
		setSelectedCandidate(candidate);
	};

	const resetForm = () => {
		setSelectedCandidate(null);
		setBenchStartDate(new Date().toISOString().split('T')[0]);
		setDailyCost("");
		setCurrency("EUR");
		setReason("");
		setNotes("");
		setWorkPercentage("");
	};

	const handleClose = () => {
		resetForm();
		onOpenChange(false);
	};

	const handleSubmit = async () => {
		if (!organizationUuid) {
			toast({
				title: t("workforce.errors.noOrganization", "Error"),
				description: t("workforce.errors.noOrganizationDesc", "Could not determine organization"),
				variant: "destructive",
			});
			return;
		}

		if (!selectedCandidate) {
			toast({
				title: t("workforce.bench.errors.noCandidate", "Consultant required"),
				description: t("workforce.bench.errors.noCandidateDesc", "Please select a consultant to add to the bench"),
				variant: "destructive",
			});
			return;
		}

		if (!dailyCost) {
			toast({
				title: t("workforce.bench.errors.noDailyCost", "Daily cost required"),
				description: t("workforce.bench.errors.noDailyCostDesc", "Please enter the daily cost for this consultant"),
				variant: "destructive",
			});
			return;
		}

		setIsSubmitting(true);

		try {
			// Check if candidate already has an active assignment or is already on bench
			const { data: existing } = await supabase
				.from('workforce')
				.select('id, status, is_bench')
				.eq('organization_uuid', organizationUuid)
				.eq('candidate_id', selectedCandidate.id)
				.or('status.in.(active,on_hold),is_bench.eq.true');

			if (existing && existing.length > 0) {
				const hasActive = existing.some(e => (e.status === 'active' || e.status === 'on_hold') && !e.is_bench);
				const hasBench = existing.some(e => e.is_bench && (e.status === 'active' || e.status === 'on_hold'));

				if (hasActive) {
					toast({
						title: t("workforce.bench.errors.hasActiveAssignment", "Has active assignment"),
						description: t("workforce.bench.errors.hasActiveAssignmentDesc", "This consultant has an active assignment and cannot be added to the bench"),
						variant: "destructive",
					});
					setIsSubmitting(false);
					return;
				}

				if (hasBench) {
					toast({
						title: t("workforce.bench.errors.alreadyOnBench", "Already on bench"),
						description: t("workforce.bench.errors.alreadyOnBenchDesc", "This consultant is already on the bench"),
						variant: "destructive",
					});
					setIsSubmitting(false);
					return;
				}
			}

			// Insert bench workforce entry
			const { error } = await supabase
				.from('workforce')
				.insert({
					organization_uuid: organizationUuid,
					candidate_id: selectedCandidate.id,
					start_date: benchStartDate,
					status: 'active',
					is_bench: true,
					bench_reason: reason || null,
					bench_daily_cost: dailyCost ? parseFloat(dailyCost) : null,
					currency,
					notes: notes || null,
					work_percentage: workPercentage ? parseInt(workPercentage) : null,
				});

			if (error) throw error;

			toast({
				title: t("workforce.bench.addSuccess", "Added to bench"),
				description: t("workforce.bench.addSuccessDesc", "Consultant has been added to the bench"),
			});

			resetForm();
			onOpenChange(false);
			onSuccess?.();
		} catch (error: any) {
			toast({
				title: t("common.error", "Error"),
				description: error.message || t("workforce.bench.errors.addFailed", "Failed to add to bench"),
				variant: "destructive",
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget)
			handleClose();
	};

	if (!open) return null;

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4"
			onClick={handleBackdropClick}
		>
			<div className="bg-white dark:bg-gray-900 rounded-sm shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col animate-slide-in border border-gray-200 dark:border-gray-700 overflow-hidden">
				{/* Header */}
				<div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 flex-shrink-0">
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center shadow-sm">
							<UserX className="h-5 w-5 text-primary-foreground" />
						</div>
						<div>
							<h2 className="text-xl font-bold text-gray-900 dark:text-white">
								{t("workforce.bench.addTitle", "Add to Bench")}
							</h2>
							<p className="text-sm text-muted-foreground">
								{t("workforce.bench.addSubtitle", "Manually track a consultant on the bench")}
							</p>
						</div>
					</div>
					<button
						type="button"
						onClick={handleClose}
						className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-800"
					>
						<X className="h-5 w-5" />
					</button>
				</div>

				{/* Content */}
				<div className="flex-1 overflow-y-auto p-6 space-y-5">
					{/* Consultant Selection */}
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
							{t("workforce.fields.consultant", "Consultant")} *
						</Label>
						<CandidateSelector
							onSelectCandidate={handleCandidateSelect}
							buttonClassName="w-full h-11"
						/>
						{selectedCandidate && (
							<div className="flex items-center gap-2 p-2 bg-muted rounded-sm border">
								<Users2 className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm font-medium">
									{selectedCandidate.name}
								</span>
							</div>
						)}
					</div>

					{/* Bench Start Date */}
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
							<Calendar className="h-4 w-4 inline mr-1" />
							{t("workforce.bench.startDate", "Bench Start Date")}
						</Label>
						<Input
							type="date"
							value={benchStartDate}
							onChange={(e) => setBenchStartDate(e.target.value)}
							className="h-11"
						/>
					</div>

					{/* Daily Cost (mandatory) */}
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
							<DollarSign className="h-4 w-4 inline mr-1" />
							{t("workforce.bench.dailyCostLabel", "Daily Cost (what you pay them)")} *
						</Label>
						<div className="flex gap-2">
							<Input
								type="number"
								min="0"
								step="0.01"
								value={dailyCost}
								onChange={(e) => setDailyCost(e.target.value)}
								placeholder="0.00"
								className="h-11 flex-1"
							/>
							<Select value={currency} onValueChange={setCurrency}>
								<SelectTrigger className="h-11 w-24">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{CURRENCY_OPTIONS.map((curr) => (
										<SelectItem key={curr} value={curr}>
											{curr}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<p className="text-xs text-muted-foreground">
							{t("workforce.bench.dailyCostHint", "Used to calculate bench costs over time")}
						</p>
					</div>

					{/* Allocation % */}
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
							{t("workforce.fields.workPercentage", "Allocation %")}
						</Label>
						<Input
							type="number"
							min="0"
							max="100"
							value={workPercentage}
							onChange={(e) => setWorkPercentage(e.target.value)}
							placeholder="100"
							className="h-11"
						/>
					</div>

					{/* Reason */}
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
							{t("workforce.bench.reason", "Reason")}
						</Label>
						<Select value={reason} onValueChange={(v) => setReason(v as BenchReason)}>
							<SelectTrigger className="h-11">
								<SelectValue placeholder={t("workforce.bench.selectReason", "Select reason...")} />
							</SelectTrigger>
							<SelectContent>
								{BENCH_REASONS.map((r) => (
									<SelectItem key={r.value} value={r.value}>
										{t(`workforce.bench.reasons.${r.value}`, r.label)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{/* Notes */}
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
							{t("workforce.bench.notes", "Notes")}
						</Label>
						<Textarea
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							placeholder={t("workforce.bench.notesPlaceholder", "Additional notes about this bench entry...")}
							rows={3}
							className="resize-none"
						/>
					</div>
				</div>

				{/* Footer */}
				<div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700 gap-3 bg-gradient-to-r from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 flex-shrink-0">
					<Button
						type="button"
						variant="outline"
						onClick={handleClose}
						disabled={isSubmitting}
						className="px-6"
					>
						{t("common.cancel", "Cancel")}
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={isSubmitting || !selectedCandidate || !dailyCost}
						className="px-6"
					>
						{isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
						{t("workforce.bench.addButton", "Add to Bench")}
					</Button>
				</div>
			</div>
		</div>
	);
};
